import { toast } from 'react-toastify'
import { NUMBER_OF_ITEMS_PER_PAGE } from '../../constants'
import { getErrorMessage } from '../../helpers'
import type {
  BaseApiResponse,
  QueryProps,
  StorefrontSection,
} from '../shared/shared.type'
import type {
  CreateVariantResponse,
  Product,
  ProductsResponse,
  SingleProductResponse,
  VariantsResponse,
} from './products.type'
import {
  AddCategoryResponse,
  CategoryToProduct,
  CreateCategoryPayload,
  CreateCategoryResponse,
  SingleCategoryResponse,
} from '../categories/categories.type'
import { apiSlice } from '../api/apiSlice'
import { ErrorType } from '../shared/shared.type'
import {
  AddProductToSubVariant,
  AddProductToSubVariantResponse,
  BulkProductPriceUpdatePayload,
  BulkProductPriceUpdateResponse,
  CreateProductPayload,
  CreateProductResponse,
  CreateSubCategory,
  CreateSubCategoryResponse,
  CreateSubVariant,
  CreateSubVariantResponse,
  CreateTagPayload,
  CreateTagResponse,
  ProductSearchQuery,
  ProductInventoryResponse,
  RemoveProductFromSubVariant,
  RemoveProductFromSubVariantResponse,
  SingleSubCategoryResponse,
  SingleSubVariantResponse,
  SubCategoriesResponse,
  SubVariantsResponse,
  TagsResponse,
  UpdateProductGiftEligibilityPayload,
  UpdateProductStockPayload,
} from './products.type'

export const productsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllProducts: builder.query<ProductsResponse, ProductSearchQuery>({
      query: (args) => {
        return {
          url: `v1/items`,
          method: 'GET',
          params: {
            isAdmin: true,
            limit: Number(args.limit) ?? NUMBER_OF_ITEMS_PER_PAGE,
            skip: args.skip,
            search: args.search,
            ...args,
          },
        }
      },
      providesTags: [{ type: 'PRODUCT', id: 'LIST' }],
    }),
    getSingleProduct: builder.query<SingleProductResponse, { id: string }>({
      query: (args) => {
        return {
          url: `v1/items/${args.id}`,
          method: 'GET',
          params: {
            isAdmin: true,
          },
        }
      },
      providesTags: (_result, _error, arg) => [{ type: 'PRODUCT', id: arg.id }],
    }),
    getProductInventory: builder.query<
      ProductInventoryResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `v1/items/${id}/inventory`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { id }) => [
        { type: 'PRODUCT_INVENTORY', id },
      ],
    }),
    getAllProductTags: builder.query<TagsResponse, QueryProps>({
      query: (args) => ({
        url: `v1/items/tags`,
        method: 'GET',
        params: {
          limit: args.limit,
          skip: args.skip,
          search: args.search,
        },
      }),
      providesTags: [{ type: 'TAG', id: 'LIST' }],
    }),
    createProductCategory: builder.mutation<
      CreateCategoryResponse,
      CreateCategoryPayload
    >({
      query: (payload) => ({
        url: `v1/items/categories`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (data: CreateCategoryResponse) => {
        return data
      },
      invalidatesTags: [{ type: 'CATEGORY', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    editProductCategory: builder.mutation<
      CreateCategoryResponse,
      { id: string; payload: Partial<CreateCategoryPayload> }
    >({
      query: (payload) => ({
        url: `v1/items/categories/${payload.id}`,
        method: 'PUT',
        body: payload.payload,
      }),
      transformResponse: (data: CreateCategoryResponse) => {
        return data
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'CATEGORY', id: arg.id },
        { type: 'CATEGORY', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    createProductTag: builder.mutation<CreateTagResponse, CreateTagPayload>({
      query: (payload) => ({
        url: `v1/items/tags`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (data: CreateTagResponse) => {
        return data
      },
      invalidatesTags: [{ type: 'TAG', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    editProductTag: builder.mutation<
      CreateTagResponse,
      { id: string; payload: CreateTagPayload }
    >({
      query: (payload) => ({
        url: `v1/items/tags/${payload.id}`,
        method: 'PUT',
        body: payload.payload,
      }),
      transformResponse: (data: CreateTagResponse) => {
        return data
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'TAG', id: arg.id },
        { type: 'TAG', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    deleteProductTag: builder.mutation<CreateTagResponse, { id: string }>({
      query: ({ id }) => ({
        url: `v1/items/tags/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'TAG', id: arg.id },
        { type: 'TAG', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    getVariants: builder.query<VariantsResponse, QueryProps>({
      query: (args) => {
        const limit = Number(args.limit) ?? NUMBER_OF_ITEMS_PER_PAGE
        return {
          url: `v1/items/variants`,
          method: 'GET',
          params: {
            limit: limit,
            page: args.page,
            search: args.search,
          },
        }
      },
      providesTags: [{ type: 'VARIANT', id: 'LIST' }],
    }),

    createVariant: builder.mutation<
      CreateVariantResponse,
      { name: string; isSubVariant: boolean }
    >({
      query: (payload) => ({
        url: `v1/items/variants`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (data: CreateVariantResponse) => {
        return data
      },
      invalidatesTags: [
        { type: 'VARIANT', id: 'LIST' },
        { type: 'SUBVARIANT', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    createProduct: builder.mutation<
      CreateProductResponse,
      CreateProductPayload
    >({
      query: (payload) => ({
        url: `v1/items/create`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'PRODUCT', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    EditProduct: builder.mutation<
      CreateProductResponse,
      { id: string; body: CreateProductPayload }
    >({
      query: (payload) => ({
        url: `v1/items/${payload.id}`,
        method: 'PUT',
        body: payload.body,
      }),
      transformResponse: (data: CreateProductResponse) => {
        return data
      },
      invalidatesTags: (result, _error, { id }) =>
        result?.success
          ? [
              { type: 'PRODUCT', id },
              { type: 'PRODUCT', id: 'LIST' },
              { type: 'PRODUCT_INVENTORY', id },
            ]
          : [],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    updateItemStock: builder.mutation<
      { success: boolean; message: string; data: null },
      { id: string; payload: UpdateProductStockPayload }
    >({
      query: (payload) => ({
        url: `v1/items/${payload.id}/stock`,
        method: 'PUT',
        body: payload.payload,
      }),
      invalidatesTags: (result, _error, { id }) =>
        result?.success
          ? [
              { type: 'PRODUCT_INVENTORY', id },
              { type: 'PRODUCT', id },
            ]
          : [],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    bulkUpdateProductPrices: builder.mutation<
      BulkProductPriceUpdateResponse,
      BulkProductPriceUpdatePayload
    >({
      query: (payload) => ({
        url: `v1/items/bulk-price`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: [{ type: 'PRODUCT', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    getSubVariants: builder.query<
      SubVariantsResponse,
      { vendorId?: string; variantId: string } & QueryProps
    >({
      query: (args) => {
        const limit = Number(args.limit) ?? NUMBER_OF_ITEMS_PER_PAGE
        return {
          url: `v1/items/subvariants`,
          method: 'GET',
          params: {
            limit: limit,
            skip: args.page ? Number(args.page) * limit - limit : 0,
            search: args.search,
            vendorId: args.vendorId,
          },
        }
      },
      providesTags: [{ type: 'SUBVARIANT', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    getSingleSubVariant: builder.query<
      SingleSubVariantResponse,
      { id: string }
    >({
      query: (args) => {
        return {
          url: `v1/items/subvariant/${args.id}`,
          method: 'GET',
        }
      },
      providesTags: (_result, _error, arg) => [
        { type: 'SUBVARIANT', id: arg.id },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    createSubVariant: builder.mutation<
      CreateSubVariantResponse,
      CreateSubVariant
    >({
      query: (payload) => ({
        url: `v1/items/create-subvariant`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'SUBVARIANT', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    addProductToSubVariant: builder.mutation<
      AddProductToSubVariantResponse,
      AddProductToSubVariant
    >({
      query: (payload) => ({
        url: `v1/items/add-product-to-subvariant`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'SUBVARIANT', id: arg.variantOptionId },
        { type: 'SUBVARIANT', id: 'LIST' },
        { type: 'PRODUCT', id: arg.productId },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    removeProductFromSubVariant: builder.mutation<
      RemoveProductFromSubVariantResponse,
      RemoveProductFromSubVariant
    >({
      query: (payload) => ({
        url: `v1/items/remove-product-from-subvariant`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'SUBVARIANT', id: arg.variantOptionId },
        { type: 'SUBVARIANT', id: 'LIST' },
        { type: 'PRODUCT', id: arg.productId },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    createSubCategory: builder.mutation<
      CreateSubCategoryResponse,
      CreateSubCategory
    >({
      query: (payload) => ({
        url: `v1/items/subcategory`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'SUBCATEGORY', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    editSubCategory: builder.mutation<
      CreateSubCategoryResponse,
      { id: string; payload: Partial<CreateSubCategory> }
    >({
      query: ({ id, payload }) => ({
        url: `v1/items/subcategory/${id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'SUBCATEGORY', id: arg.id },
        { type: 'SUBCATEGORY', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    getSubCategories: builder.query<
      SubCategoriesResponse,
      QueryProps & {
        categories?: string
        isAdmin?: boolean
        section?: StorefrontSection
      }
    >({
      query: (args) => {
        const limit = Number(args.limit) ?? NUMBER_OF_ITEMS_PER_PAGE
        return {
          url: `v1/items/subcategory`,
          method: 'GET',
          params: { ...args },
        }
      },
      providesTags: [{ type: 'SUBCATEGORY', id: 'LIST' }],
    }),

    fetchProductsBySubcategory: builder.query<
      {
        success: boolean
        message: string
        data: { totalItems: number; data: Product[] }
      },
      { subcategoryId: string }
    >({
      query: ({ subcategoryId }) => ({
        url: `v1/items?subcategoryId=${subcategoryId}`,
        method: 'GET',
      }),
      providesTags: [{ type: 'PRODUCT', id: 'LIST' }],
    }),

    addCategoryToProducts: builder.mutation<
      AddCategoryResponse,
      CategoryToProduct
    >({
      query: (body) => ({
        url: `v1/items/add-category-to-products`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'PRODUCT', id: 'LIST' },
        { type: 'CATEGORY', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(
            data?.message || 'Products successfully added to category!',
          )
        } catch (error) {
          toast.error('Failed to add products to category')
        }
      },
    }),

    removeProductsFromSubCategory: builder.mutation<
      BaseApiResponse,
      { id: string; products: string[] }
    >({
      query: ({ id, ...payload }) => ({
        url: `v1/items/subcategory/${id}/remove`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'SUBCATEGORY', id: arg.id },
        { type: 'SUBCATEGORY', id: 'LIST' },
        { type: 'PRODUCT', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    bulkUploadProducts: builder.mutation<
      {
        success: boolean
        data: null
        message: string
      },
      FormData
    >({
      query: (payload) => ({
        url: `v1/items/upload-bulk`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'PRODUCT', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(error as string)
        }
      },
    }),

    reorderCategories: builder.mutation<
      BaseApiResponse,
      { categoryIds: string[] }
    >({
      query: (payload) => ({
        url: `v1/admin/categories/reorder`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'CATEGORY', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(error as string)
        }
      },
    }),

    reorderSubCategories: builder.mutation<
      BaseApiResponse,
      { categoryId: string; payload: { subcategoryIds: string[] } }
    >({
      query: ({ categoryId, payload }) => ({
        url: `v1/admin/categories/${categoryId}/subcategories/reorder`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'SUBCATEGORY', id: 'LIST' },
        { type: 'CATEGORY', id: arg.categoryId },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
        } catch (error) {
          toast.error(error as string)
        }
      },
    }),

    getSingleSubCategory: builder.query<
      SingleSubCategoryResponse,
      { subcategoryId: string; isAdmin?: boolean }
    >({
      query: (args) => {
        return {
          url: `v1/items/subcategory`,
          method: 'GET',
          params: {
            ...args,
          },
        }
      },
      providesTags: (_result, _error, arg) => [
        { type: 'SUBCATEGORY', id: arg.subcategoryId },
      ],
    }),

    getSingleCategory: builder.query<
      SingleCategoryResponse,
      { categoryId: string }
    >({
      query: (args) => {
        return {
          url: `v1/items/categories`,
          method: 'GET',
          params: {
            ...args,
            isAdmin: true,
          },
        }
      },
      providesTags: (_result, _error, arg) => [
        { type: 'CATEGORY', id: arg.categoryId },
      ],
    }),

    updateProductGiftEligibility: builder.mutation<
      BaseApiResponse,
      UpdateProductGiftEligibilityPayload
    >({
      query: ({ productId, ...payload }) => ({
        url: `v2/items/free-gift/products/${productId}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'PRODUCT', id: arg.productId },
        { type: 'PRODUCT', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(error as string)
        }
      },
    }),
  }),
})

export const {
  useFetchProductsBySubcategoryQuery,
  useGetAllProductsQuery,
  useLazyGetAllProductsQuery,
  useGetAllProductTagsQuery,
  useLazyGetAllProductTagsQuery,
  useCreateProductCategoryMutation,
  useEditProductCategoryMutation,
  useCreateProductTagMutation,
  useEditProductTagMutation,
  useDeleteProductTagMutation,
  useLazyGetSingleProductQuery,
  useGetSingleProductQuery,
  useGetProductInventoryQuery,
  useGetVariantsQuery,
  useCreateVariantMutation,
  useCreateProductMutation,
  useEditProductMutation,
  useUpdateItemStockMutation,
  useBulkUpdateProductPricesMutation,
  useGetSubVariantsQuery,
  useLazyGetSubVariantsQuery,
  useGetSingleSubVariantQuery,
  useLazyGetSingleSubVariantQuery,
  useCreateSubVariantMutation,
  useAddProductToSubVariantMutation,
  useRemoveProductFromSubVariantMutation,
  useCreateSubCategoryMutation,
  useEditSubCategoryMutation,
  useGetSubCategoriesQuery,
  useBulkUploadProductsMutation,
  useAddCategoryToProductsMutation,
  useReorderCategoriesMutation,
  useReorderSubCategoriesMutation,
  useGetSingleSubCategoryQuery,
  useGetSingleCategoryQuery,
  useRemoveProductsFromSubCategoryMutation,
  useUpdateProductGiftEligibilityMutation,
} = productsApiSlice
